import * as Sentry from '@sentry/vue'
import { insidePoly } from '@/helpers'
import mitt from 'mitt'

import { PushNotifications } from '@capacitor/push-notifications'
import { Capacitor } from '@capacitor/core'
const BackgroundGeolocation = Capacitor.registerPlugin('BackgroundGeolocation') // @capacitor-community/background-geolocation

export default class Tracer {
  #emitter = mitt()
  #geoID

  #traceRate
  #traceInterval

  #position = {}
  #fields
  #lastField
  #currentField

  #getNetwork
  #getBattery

  #additions

  #traceStore

  constructor ({ additions = {}, traceRate, fields, getBattery, getNetwork } = {}) {
    this.#fields = fields
    this.#additions = Object.freeze(additions)

    this.#traceRate = traceRate
    this.#getNetwork = getNetwork
    this.#getBattery = getBattery
  }

  on (event, callback) { this.#emitter.on(event, callback) }
  off (event, callback) { this.#emitter.off(event, callback) }

  static async setup (includeGPS = false) {
    if (Capacitor.isNativePlatform()) {
      const permStatus = await PushNotifications.checkPermissions()
      if (permStatus.receive === 'prompt') PushNotifications.requestPermissions()
    }

    if (includeGPS) {
      if (Capacitor.isPluginAvailable('BackgroundGeolocation')) {
        const result = await BackgroundGeolocation.addWatcher(
          {
            backgroundTitle: 'Hintergrund-Lokalisierung aktiv',
            backgroundMessage: 'Der bacchus.Tracker erfasst im Hintergrund Ihre Position.'
          },
          (pos, error) => {
            if (error && error.code === 'NOT_AUTHORIZED') return BackgroundGeolocation.openSettings()
          }
        )
          .then(watcherID => {
            BackgroundGeolocation.removeWatcher({ id: watcherID })
            return true
          })
          .catch(() => false)

        if (!result) return false
      }
    }

    return true
  }

  async start (traceStore) {
    this.#traceStore = traceStore

    await Tracer.setup()

    if (Capacitor.isPluginAvailable('BackgroundGeolocation')) {
      this.#geoID = await BackgroundGeolocation.addWatcher(
        {
          backgroundTitle: 'Hintergrund-Lokalisierung aktiv',
          backgroundMessage: 'Der bacchus.Tracker erfasst im Hintergrund Ihre Position.'
        },
        (pos, error) => {
          // Pings every Second?
          if (error && error.code === 'NOT_AUTHORIZED') return BackgroundGeolocation.openSettings()
          if (error) return captureGeoError(error)

          this.#onPositionUpdate(pos)
          this.#tick()
        }
      )
    } else {
      this.#traceInterval = setInterval(() => { this.#tick() }, 1000)
    }
  }

  simulatePosition ({ lng, lat }) {
    this.#onPositionUpdate({
      time: Date.now(),
      longitude: lng,
      latitude: lat,
      accuracy: 1,
      heading: 1,
      speed: 8
    })
    this.#tick()
  }

  async stop () {
    this.#traceStore = null
    this.#emitter.all.clear()
    if (this.#traceInterval) clearInterval(this.#traceInterval)
    if (this.#geoID) await BackgroundGeolocation.removeWatcher({ id: this.#geoID })
  }

  get () {
    return Object.freeze({
      ...baseTrace,
      ...this.#additions,
      recorded_datetime: Date.now(),
      position_timestamp: this.#position.timestamp,
      position_longitude: this.#position.longitude,
      position_latitude: this.#position.latitude,
      position_accuracy: this.#position.accuracy,
      position_heading: this.#position.heading,
      position_speed: this.#position.speed,
      field: this.#currentField,
      network: this.#getNetwork(),
      battery_percentage: this.#getBattery().level,
      battery_charging: this.#getBattery().isCharging
    })
  }

  #tick () {
    const trace = this.get()

    this.#emitter.emit('trace', trace)

    if (!this.#traceStore) return
    if (this.#traceStore.isAwaitingTrace(trace, this.#traceRate)) this.#traceStore.push(trace)
    if (this.#traceStore.isAwaitingSync()) this.#traceStore.sync()
  }

  #onPositionUpdate (pos) {
    if (this.#position.timestamp === pos.time) return

    this.#position = Object.freeze({
      timestamp: pos.time,
      longitude: pos.longitude,
      latitude: pos.latitude,
      accuracy: pos.accuracy,
      heading: pos.bearing,
      speed: pos.speed
    })

    this.#emitter.emit('position', this.#position)

    // Find Current Field
    if (!this.#fields) return
    const hit = findField([this.#position.longitude, this.#position.latitude], this.#fields, this.#lastField)
    if (hit) this.#lastField = hit
    this.#currentField = hit ? hit.id : null
  }
}

const baseTrace = {
  recorded_datetime: undefined,
  position_timestamp: undefined,
  position_longitude: undefined,
  position_latitude: undefined,
  position_accuracy: undefined,
  position_heading: undefined,
  position_speed: undefined,
  field: undefined,
  network: undefined,
  battery_percentage: undefined,
  battery_charging: undefined
}

function findField ([x, y] = [], fields, lastField) {
  if (x === undefined || y === undefined) return
  if (lastField && insidePoly([x, y], lastField.coords)) return lastField

  for (const field of fields) {
    if (field.extentX && (field.extentX[0] > x || field.extentX[1] < x)) continue
    if (field.extentY && (field.extentY[0] > y || field.extentY[1] < y)) continue
    if (insidePoly([x, y], field.coords)) return field
  }
}

function captureGeoError (err) {
  console.error(err) // eslint-disable-line no-console
  Sentry.captureException(err)
}
